<template>
  <v-container fluid>
    <v-row>
      <v-col cols="6"> </v-col>
      <v-col cols="6">
        <h2 style="text-align: center">{{ date }} {{ time }}</h2>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6" v-for="(data, index) in charts[0]" :key="`${index}${data}`">
        <v-row v-if="charts[1][index].title === 'Installed Capacity By Fuel Type'">
          <v-col cols="12" sm="6">
            <v-select
              v-model="installedCapChartType"
              label="Chart Type"
              :items="installedCapChartTypeItems"
            />
          </v-col>
        </v-row>

        <Plotly
          :data="data"
          :layout="charts[1][index]"
          :toImageButtonOptions="chartDownloadName(caseName, 'AA', charts[1][index].title)"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import AADashboardMixin from "@/mixins/AADashboard";
import { chartDownloadName, createAxis, filterLowChartValues } from "@/plugins/utils";
import { VCol, VContainer, VRow } from "vuetify/lib";

export default {
  mixins: [AADashboardMixin],
  name: "AADashboard",
  components: {
    VCol,
    VContainer,
    VRow,
    Plotly: () => import("@/components/charts/Plotly.vue"),
  },
  props: {
    caseOutputs: { type: Object, required: false, default: () => ({}) },
    inputCase: { type: Object, required: false, default: () => ({}) },
  },
  data() {
    return {
      caseName: this.inputCase.name,
      installedCapChartType: "bar",
      installedCapChartTypeItems: [
        { text: "Stacked Bar Chart", value: "bar" },
        { text: "Stacked Area Chart", value: "stacked" },
      ],
      colorSet: {
        URANIUM: "#FFA500",
        COAL: "#000000",
        HYDRO: "#00008B",
        WIND: "#008000",
        DISTILLATE_FUEL_OIL: "#696969",
        SOLAR_PV: "#FFD700",
        NATURAL_GAS: "#D3D3D3",
        HYDRO_ROR: "#0000FF",
        BIOMASS: "#8B4513",
      },
      layouts: [
        // Power & Energy
        {
          visible: null,
          title: "Power & Energy",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "MW",
            },
          },
          yaxis2: {
            rangemode: "tozero",
            title: "GWh",
            overlaying: "y",
            side: "right",
          },
          legend: { orientation: "h" },
        },

        // Renewable Expansion

        {
          visible: null,
          title: "Renewable Expansion",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "Capacity (MW)",
            },
          },
          showlegend: true,
          legend: { orientation: "h" },
        },
        // Storage Expansion

        {
          visible: null,
          barmode: "relative",
          title: "Storage Expansion",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "Capacity (MW)",
            },
          },
          showlegend: true,
          legend: { orientation: "h" },
        },
        // Installed Capacity By Fuel Type
        {
          visible: null,

          title: "Installed Capacity By Fuel Type",
          barmode: "stack",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "Capacity (MW)",
            },
          },
          showlegend: true,
          legend: { orientation: "h" },
        },
        // Expansion & Retirements
        {
          visible: null,

          title: "Expansion & Retirement",
          barmode: "relative",
          xaxis: {
            tickformat: "d",
            title: {
              text: "Years",
              standoff: 4,
            },
          },
          yaxis: {
            rangemode: "tozero",
            title: {
              text: "MW",
            },
          },
          annotations: [
            {
              xref: "paper",
              x: 0.01,
              y: 100,
              text: "Expansion",
              yanchor: "bottom",
              showarrow: false,
              bgcolor: "#ffffff",
              textangle: -90,
            },
            {
              xref: "paper",
              x: 0.01,
              y: -100,
              text: "Retirement",
              yanchor: "top",
              showarrow: false,
              bgcolor: "#ffffff",
              textangle: -90,
            },
          ],
          showlegend: true,
          legend: { orientation: "h" },
        },
      ],
    };
  },
  computed: {
    // Data 1
    dataPowerEnergy() {
      return [
        {
          label: "Peak Demand",
          data: this.getOutput("peak_demand", this.output),
          yaxis: "y1",
        },
        {
          label: "Installed Capacity",
          data: this.getOutput("installed_capacity", this.output),
          yaxis: "y1",
        },
        {
          label: "Capacity Requirement",
          data: this.getOutput("capacity_requirement", this.output),
          yaxis: "y1",
        },
        {
          label: "Annual Energy Demand",
          data: this.getOutput("energy_demand", this.output),
          yaxis: "y2",
        },
      ].map((dataset) => ({
        yaxis: dataset.yaxis,
        name: dataset.label,
        type: "scatter",
        ...createAxis(dataset.data, this.simulationYearArray),
      }));
    },
    // Data 2
    dataRenewableInstalled() {
      const traditionalRenewables = [
        {
          label: "Solar Installed",
          data: this.installedByFuelType.SOLAR_PV,
          color: "SOLAR_PV",
        },
        {
          label: "Wind Installed",
          data: this.installedByFuelType.WIND,
          color: "WIND",
        },
        {
          label: "Hydro Installed",
          data: this.installedByFuelType.HYDRO,
          color: "HYDRO",
        },
        {
          label: "Hydro Ror Installed",
          data: this.installedByFuelType.HYDRO_ROR,
          color: "HYDRO_ROR",
        },
      ];

      // Add thermal generators marked as renewable grouped by source (e.g., biomass)
      const additionalRenewablesBySource = {};
      const traditionalRenewableSources = ["SOLAR_PV", "HYDRO", "HYDRO_ROR", "WIND"];
      if (this.inputCase && this.inputCase.input.generators && this.output.generators) {
        this.output.generators.forEach((outputGen) => {
          if (traditionalRenewableSources.includes(outputGen.source)) {
            return; // Skip if already included by fuel type
          }
          const inputGen = this.inputCase.input.generators.find((g) => g.id === outputGen.id);
          if (inputGen && inputGen.count_as_renewable === true) {
            if (!additionalRenewablesBySource[outputGen.source]) {
              additionalRenewablesBySource[outputGen.source] = [];
            }
            additionalRenewablesBySource[outputGen.source].push(
              Object.values(outputGen.installed_capacity)
            );
          }
        });
      }

      // Convert grouped renewable sources to chart format
      const additionalRenewables = Object.keys(additionalRenewablesBySource).map((source) => {
        const sourceData = additionalRenewablesBySource[source];
        // Sum all generators of this source type across years
        const aggregatedData = sourceData.reduce((acc, generatorData) => {
          return generatorData.map((value, yearIndex) => (acc[yearIndex] || 0) + value);
        }, []);

        return {
          label: `${this.formatString(source)} Installed`,
          data: aggregatedData,
          color: source,
        };
      });

      const allRenewables = [...traditionalRenewables, ...additionalRenewables];
      const sum = allRenewables
        .map((el) => el.data.reduce((acc, item) => acc + item, 0))
        .reduce((acc, item) => acc + item, 0);

      return allRenewables
        .map((dataset) => ({
          marker: { color: this.colorSet[dataset.color] },
          name: dataset.label,
          type: "scatter",
          ...createAxis(dataset.data, this.simulationYearArray),
        }))
        .filter(filterLowChartValues(sum * 0.001));
    },
    // Data 3
    dataStorageInstalled() {
      const sum = Object.values(this.storageInstalled)
        .map((el) => el.reduce((acc, item) => acc + item, 0))
        .reduce((acc, item) => acc + item, 0);
      return Object.values(this.storageInstalled)
        .map((item, index) => ({
          ...createAxis(item, this.simulationYearArray),
          name: Object.keys(this.storageInstalled)[index].replace(/_/g, " "),
          type: "bar",
        }))
        .filter(filterLowChartValues(sum * 0.001));
    },
    // Data 4
    dataInstalledByFuelType() {
      const sum = Object.values(this.installedByFuelType)
        .map((el) => el.reduce((acc, item) => acc + item, 0))
        .reduce((acc, item) => acc + item, 0);
      return Object.values(this.installedByFuelType)
        .map((item, index) => ({
          ...createAxis(item, this.simulationYearArray),
          ...(this.installedCapChartType === "bar"
            ? { marker: { color: this.colorSet[Object.keys(this.installedByFuelType)[index]] } }
            : { fillcolor: this.colorSet[Object.keys(this.installedByFuelType)[index]] }),
          name: this.formatString(Object.keys(this.installedByFuelType)[index]),
          ...(this.installedCapChartType === "bar" ? {} : { stackgroup: "one", mode: "none" }),
          type: this.installedCapChartType,
        }))
        .filter(filterLowChartValues(sum * 0.001));
    },
    dataExpansionsRetirements() {
      const sum = Object.values(this.installedByFuelType)
        .map((el) => el.reduce((acc, item) => (item < 0 ? acc - item : acc + item), 0))
        .reduce((acc, item) => acc + item, 0);
      return Object.values(this.expensionsRetirements)
        .map((item, index) => ({
          fillcolor: this.colorSet[Object.keys(this.expensionsRetirements)[index]],
          ...createAxis(item, this.simulationYearArray.slice(1, this.simulationYearArray.length)),
          name: this.formatString(Object.keys(this.expensionsRetirements)[index]),
          type: "bar",
        }))
        .filter(filterLowChartValues(sum * 0.001));
    },
    datas() {
      return [
        this.dataPowerEnergy,
        this.dataRenewableInstalled,
        this.dataStorageInstalled,
        this.dataInstalledByFuelType,
        this.dataExpansionsRetirements,
      ];
    },

    // Charts for template
    charts() {
      this.datas.forEach((data, index) => {
        if (data.length > 0) {
          this.layouts[index].visible = true;
        } else {
          this.layouts[index].visible = false;
        }
      });

      return [
        this.datas.filter((chart) => chart.length > 0),
        this.layouts.filter((layout) => layout.visible === true),
      ];
    },
  },
  watch: {},
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    createAxis,
    filterLowChartValues,
    chartDownloadName,
  },
};
</script>

<style></style>
